// One combat encounter. Runs as a sub-scene that ZoneScene launches.
//
// Drives the state machine on combatBus; the Preact CombatHud subscribes and
// presents. We do not duplicate UI in Phaser — the canvas just draws the hero,
// monster, and projectile/particle effects.

import Phaser from 'phaser';
import { combatBus } from '../../app/combatBus';
import { WebSpeechRecognizer, speakHanzi } from '../../speech/webSpeech';
import { ensureMicAccess, readMicPermission } from '../../speech/mic';
import { AzureSpeechClient } from '../../speech/azureClient';
import { judgeAttempt, judgeAzureAttempt, type Outcome } from '../../speech/scoring';
import { applyAttempt } from '../../learning/srs';
import { nextScaffoldStep, type ScaffoldStep } from '../../learning/failureScaffold';
import { appState } from '../../app/state';
import { db, type Attempt } from '../../data/db';
import { COLORS, drawWizard, drawMonster, monsterMeta, drawParallaxBg } from '../util/draw';
import type { Card } from '../../content/cardSchema';

export interface CombatInit {
  card: Card;
  monsterKind: number;
  monsterMaxHp: number;
  baseDamage: number;
  sessionId: string;
  onResolved: (result: CombatResult) => void;
}

export interface CombatResult {
  passed: boolean;
  outcome: Outcome;
  score: number;
  reQueue: boolean;        // true on step-3 scaffold; ZoneScene re-uses card later
  ranAway: boolean;        // monster fled (step-3) — counts as encounter completed
  xpGained: number;
}

export class CombatScene extends Phaser.Scene {
  private sr = new WebSpeechRecognizer();
  private cfg!: CombatInit;
  private retryStep: ScaffoldStep = 0;
  private heroGfx!: Phaser.GameObjects.Graphics;
  private monsterGfx!: Phaser.GameObjects.Graphics;
  private unsubAction?: () => void;
  private monsterHp = 0;
  private heroHpAtStart = 0;

  constructor() { super({ key: 'Combat' }); }

  init(data: CombatInit) { this.cfg = data; }

  create() {
    const { width, height } = this.scale;
    drawParallaxBg(this, 0x281b50);

    const meta = monsterMeta(this.cfg.monsterKind);
    const progress = appState.get().progress!;
    this.monsterHp = this.cfg.monsterMaxHp;
    this.heroHpAtStart = progress.hp;

    // Draw hero (left) and monster (right)
    this.heroGfx = this.add.graphics();
    drawWizard(this.heroGfx, width * 0.28, height * 0.62, 1.3);

    this.monsterGfx = this.add.graphics();
    drawMonster(this.monsterGfx, width * 0.72, height * 0.62, this.cfg.monsterKind, 1.4);

    // Floor line
    this.add.rectangle(width / 2, height * 0.78, width, 4, COLORS.heroHat, 1);

    // Initialize bus state
    combatBus.set({
      phase: 'show-card',
      card: this.cfg.card,
      retryStep: 0,
      showPinyin: false,
      showEnglish: false,
      lastScore: null,
      lastOutcome: null,
      lastHeard: '',
      heroHp: progress.hp,
      heroMaxHp: progress.maxHp,
      monsterHp: this.monsterHp,
      monsterMaxHp: this.cfg.monsterMaxHp,
      monsterName: meta.name,
      monsterEmoji: meta.emoji,
    });

    this.unsubAction = combatBus.onAction((a) => {
      if (a.kind === 'pressMic') this.startListening();
      else if (a.kind === 'releaseMic') this.stopListening();
      else if (a.kind === 'replayAudio') speakHanzi(this.cfg.card.hanzi);
      else if (a.kind === 'sageWhisper') this.useSageWhisper();
      else if (a.kind === 'grantMic') this.handleGrantMic();
      else if (a.kind === 'exit') this.flee();
    });

    this.events.on('shutdown', () => this.cleanup());
    this.events.on('destroy', () => this.cleanup());

    // Probe mic state on entry so the HUD can show a "Grant mic" banner if
    // permission hasn't been granted yet. iOS Safari silently produces no
    // recognition result when the mic isn't permitted, so we have to surface
    // this proactively or the user blames the game.
    readMicPermission().then((state) => combatBus.set({ micState: state }));
  }

  private async handleGrantMic() {
    const result = await ensureMicAccess();
    combatBus.set({ micState: result.state, micLastError: result.error });
  }

  private cleanup() {
    this.unsubAction?.();
    this.sr.cancel();
    this.azureClient?.cancel();
    this.azureClient = null;
    // Note: do NOT reset combatBus here — Phaser's shutdown event fires AFTER
    // the next CombatScene's create() has overwritten the state, so resetting
    // would wipe the new encounter's data. AppShell hides the HUD whenever
    // the player isn't in a zone screen, which handles overlay cleanup.
  }

  // Azure REST client lives on the scene so press/release can split into two
  // calls (startRecording / stopAndAssess) with the recording active in between.
  private azureClient: AzureSpeechClient | null = null;
  private listenStartTs = 0;

  private async startListening() {
    const settings = appState.get().settings;
    const useAzure = settings?.asrProvider === 'azure' && settings.azureKey && settings.azureRegion;

    if (useAzure) {
      // REST audio capture starts immediately on press; encoding + POST happens
      // on release inside stopListening(). No SDK timing race.
      this.azureClient = new AzureSpeechClient({ key: settings!.azureKey!, region: settings!.azureRegion! });
      combatBus.set({ phase: 'listening' });
      this.listenStartTs = performance.now();
      try {
        await this.azureClient.startRecording();
      } catch (e) {
        console.error('Azure startRecording failed:', e);
        this.azureClient = null;
        return this.handleResult({ transcript: '', score: 0, latencyMs: 0, outcome: 'miss' });
      }
      return;
    }

    // Web Speech path remains as a fallback. iPadOS Safari does not transcribe
    // zh-CN reliably — the Settings panel warns about this and recommends Azure.
    if (!this.sr.supported) {
      console.warn('Web Speech not supported — simulating attempt for demo.');
      return this.handleResult({ transcript: this.cfg.card.hanzi, score: 90, latencyMs: 200 });
    }
    combatBus.set({ phase: 'listening' });
    this.listenStartTs = performance.now();
    const result = await this.sr.start('zh-CN');
    if (!result) {
      return this.handleResult({ transcript: '', score: 0, latencyMs: 0, outcome: 'miss' });
    }
    const judged = judgeAttempt(
      this.cfg.card.hanzi,
      this.cfg.card.pinyinNum,
      result.transcript,
      appState.get().profile?.scoreFloor ?? 30,
    );
    await this.handleResult({
      transcript: result.transcript,
      score: judged.score,
      latencyMs: result.latencyMs,
      outcome: judged.outcome,
    });
  }

  private async stopListening() {
    const scoreFloor = appState.get().profile?.scoreFloor ?? 30;
    combatBus.set({ phase: 'scoring' });

    if (this.azureClient) {
      // Finalize the recording, encode WAV, POST to Azure, score the result.
      try {
        const azureResult = await this.azureClient.stopAndAssess(this.cfg.card.hanzi);
        const latencyMs = Math.round(performance.now() - this.listenStartTs);
        if (!azureResult) {
          console.warn('Azure assessment returned null:', this.azureClient.lastError);
          this.azureClient = null;
          return this.handleResult({ transcript: '', score: 0, latencyMs, outcome: 'miss' });
        }
        const judged = judgeAzureAttempt(this.cfg.card.hanzi, this.cfg.card.pinyinNum, azureResult, scoreFloor);
        this.azureClient = null;
        return this.handleResult({
          transcript: judged.heardHanzi,
          score: judged.score,
          latencyMs,
          outcome: judged.outcome,
        });
      } catch (e) {
        console.error('Azure stopAndAssess error:', e);
        this.azureClient = null;
        return this.handleResult({ transcript: '', score: 0, latencyMs: Math.round(performance.now() - this.listenStartTs), outcome: 'miss' });
      }
    }

    this.sr.stop();
    // Safety net for Web Speech: if onresult/onend never fire (rare iOS quirk),
    // resolve the encounter ourselves after 2s so the loop doesn't hang.
    this.time.delayedCall(2000, () => {
      if (combatBus.get().phase === 'scoring') {
        this.handleResult({ transcript: '', score: 0, latencyMs: 2000, outcome: 'miss' });
      }
    });
  }

  private async handleResult(r: { transcript: string; score: number; latencyMs: number; outcome?: Outcome }) {
    const profile = appState.get().profile!;
    const judged = r.outcome
      ? { score: r.score, outcome: r.outcome }
      : (() => {
          const j = judgeAttempt(this.cfg.card.hanzi, this.cfg.card.pinyinNum, r.transcript, profile.scoreFloor ?? 30);
          return { score: j.score, outcome: j.outcome };
        })();

    const action = nextScaffoldStep(this.retryStep, judged.outcome);

    // Log attempt
    const attempt: Attempt = {
      profileId: profile.id,
      cardId: this.cfg.card.id,
      sessionId: this.cfg.sessionId,
      ts: Date.now(),
      score: judged.score,
      heardHanzi: r.transcript,
      heardPinyin: '',
      latencyMs: r.latencyMs,
      outcome: judged.outcome,
      retryStep: this.retryStep <= 2 ? (this.retryStep as 0 | 1 | 2) : 2,
      passed: action.done && (judged.outcome === 'critical' || judged.outcome === 'hit' || judged.outcome === 'glance'),
    };
    await db.attempts.add(attempt);

    // SRS update (only on the kid's actual best attempt; we update on every
    // attempt for now and let the SM-2 ease curve absorb it).
    await applyAttempt(profile.id, this.cfg.card.id, judged.score);

    // Apply damage to monster / self
    const isHit = judged.outcome === 'critical' || judged.outcome === 'hit' || judged.outcome === 'glance';
    if (isHit) {
      const mult = judged.outcome === 'critical' ? 1.5 : judged.outcome === 'hit' ? 1.0 : 0.6;
      const dmg = Math.max(1, Math.round(this.cfg.baseDamage * mult));
      this.monsterHp = Math.max(0, this.monsterHp - dmg);
      this.spawnProjectile();
    }
    // Self-damage comes from the outcome itself (miss=small nibble, fumble=
    // bigger). The failure scaffold's selfDamagePct was a separate concept
    // that we've folded into the outcome multipliers for clarity. If a kid
    // can't get any score above 30 for five attempts in a row, they'll
    // game-over and be returned to the academy — important escape hatch
    // when mic isn't working.
    if (judged.score < 50) {
      const SELF_DAMAGE_BY_OUTCOME: Record<typeof judged.outcome, number> = {
        critical: 0, hit: 0, glance: 0, miss: 0.15, fumble: 0.25,
      };
      const pct = SELF_DAMAGE_BY_OUTCOME[judged.outcome] ?? 0;
      if (pct > 0) {
        const progress = appState.get().progress!;
        const selfDmg = Math.max(1, Math.round(pct * progress.maxHp));
        progress.hp = Math.max(0, progress.hp - selfDmg);
        await db.progress.put(progress);
        await appState.refreshProgress();
        this.cameras.main.shake(180, 0.005);
      }
    }

    combatBus.set({
      phase: 'feedback',
      lastScore: judged.score,
      lastOutcome: judged.outcome,
      lastHeard: r.transcript,
      monsterHp: this.monsterHp,
      heroHp: appState.get().progress!.hp,
    });

    // Wait for the player to see the feedback, then act on the scaffold
    this.time.delayedCall(900, () => this.advanceAfterFeedback(action, judged.outcome));
  }

  private advanceAfterFeedback(action: ReturnType<typeof nextScaffoldStep>, outcome: Outcome) {
    if (this.monsterHp <= 0) {
      return this.resolve({ passed: true, outcome, score: combatBus.get().lastScore ?? 100, reQueue: false, ranAway: false, xpGained: 0 });
    }
    if (action.done) {
      // Either passed (good outcome) or hit step-3 scaffold (monster flees).
      const flee = action.show.kind === 'choral';
      if (flee) {
        // Choral step: play model audio so kid says it WITH the recording.
        combatBus.set({ phase: 'scaffold-choral', showPinyin: true, showEnglish: true });
        speakHanzi(this.cfg.card.hanzi);
        this.time.delayedCall(1800, () => {
          this.resolve({ passed: false, outcome, score: combatBus.get().lastScore ?? 0, reQueue: true, ranAway: true, xpGained: 0 });
        });
      } else {
        this.resolve({ passed: outcome === 'critical' || outcome === 'hit' || outcome === 'glance', outcome, score: combatBus.get().lastScore ?? 0, reQueue: false, ranAway: false, xpGained: 0 });
      }
      return;
    }

    // Scaffold step 1 or 2 — kid retries.
    this.retryStep = action.step;
    if (action.show.kind === 'play-audio') {
      combatBus.set({ phase: 'scaffold-audio', retryStep: this.retryStep });
      speakHanzi(this.cfg.card.hanzi);
      this.time.delayedCall(1100, () => combatBus.set({ phase: 'show-card' }));
    } else if (action.show.kind === 'pinyin-and-stroke') {
      combatBus.set({ phase: 'scaffold-pinyin', retryStep: this.retryStep, showPinyin: true, showEnglish: true });
      speakHanzi(this.cfg.card.hanzi, 0.7);
      this.time.delayedCall(900, () => combatBus.set({ phase: 'show-card' }));
    }
  }

  private spawnProjectile() {
    const { width, height } = this.scale;
    const startX = width * 0.32, startY = height * 0.55;
    const endX = width * 0.7, endY = height * 0.62;
    const dot = this.add.circle(startX, startY, 8, 0xffe27a);
    this.tweens.add({
      targets: dot,
      x: endX, y: endY,
      duration: 260,
      ease: 'Sine.easeIn',
      onComplete: () => {
        dot.destroy();
        this.flashMonster();
      },
    });
  }

  private flashMonster() {
    this.monsterGfx.setAlpha(0.4);
    this.time.delayedCall(100, () => this.monsterGfx.setAlpha(1));
    if (this.monsterHp <= 0) {
      this.tweens.add({ targets: this.monsterGfx, alpha: 0, y: 30, duration: 320, ease: 'Quad.easeIn' });
    }
  }

  private async useSageWhisper() {
    const progress = appState.get().progress!;
    const limit = 3;
    if ((progress.sageWhisperUsedToday ?? 0) >= limit) {
      // out of charges — flash hint via brief feedback
      combatBus.set({ phase: 'feedback', lastScore: combatBus.get().lastScore, lastOutcome: 'miss', lastHeard: '(no whispers left today)' });
      this.time.delayedCall(900, () => combatBus.set({ phase: 'show-card' }));
      return;
    }
    progress.sageWhisperUsedToday = (progress.sageWhisperUsedToday ?? 0) + 1;
    await db.progress.put(progress);
    await appState.refreshProgress();
    combatBus.set({ showPinyin: true, showEnglish: true });
    speakHanzi(this.cfg.card.hanzi, 0.75);
  }

  private flee() {
    this.resolve({ passed: false, outcome: 'miss', score: 0, reQueue: true, ranAway: true, xpGained: 0 });
  }

  private resolve(result: CombatResult) {
    combatBus.set({ phase: 'resolved' });
    this.cleanup();
    this.cfg.onResolved(result);
    this.scene.stop();
  }
}
